import { Box, Center, Flex, Spinner, Table } from '@chakra-ui/react';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { GiArchiveResearch } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { RowExpand } from './RowExpand';
import { Checkbox, EmptyState } from '../../ui';
import { BasePagination } from '../BasePagination';
import { trans } from '@/i18n';
import { IColumn } from '@/types';
import { SortIcon } from '@/components/ui/sort-icon';
import { SCROLL_NEAR_BOTTOM_THRESHOLD } from '@/constants/common';

interface IRowSelection<T> {
  onChange: (selectedData: T[]) => void;
  selectedData: T[];
  textSelectAll?: string;
  columnWidth?: string;
  columnClassName?: string;
  hideCheckAll?: boolean;
}

interface IProps<T> {
  loading?: boolean;
  data: T[];
  columns: IColumn<T>[];
  rowKey: keyof T;
  total?: number;
  onPageChange?: (page: number) => void;
  loadMore?: () => void;
  size?: number;
  page?: number;
  renderExpand?: (item: T, expanded: boolean) => React.ReactNode;
  hideNoData?: boolean;
  hideHeader?: boolean;
  rowSelection?: IRowSelection<T>;
  scroll?: boolean;
  maxH?: string | number;
  minH?: string | number;
  rowClick?: (item: T) => void;
  editPath?: string;
  disableRowClickExpand?: boolean;
  className?: string;
  renderFooter?: ReactNode;
  onSort?: (orderKey: string, orderBy: 'asc' | 'desc') => void;
  clientId?: string;
}

function BaseTable<T>({
  loading,
  data,
  columns,
  rowKey,
  total,
  onPageChange,
  loadMore,
  size,
  page,
  renderExpand,
  hideNoData,
  hideHeader,
  rowSelection,
  scroll,
  maxH,
  minH,
  rowClick,
  editPath,
  disableRowClickExpand,
  className,
  renderFooter,
  onSort,
  clientId,
}: IProps<T>) {
  const hasSelection = !!rowSelection;
  const hasExpand = !!renderExpand;
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const sortConfig = useRef<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  const pageDataIds = useMemo(() => data.map((item) => item[rowKey] as number), [data, rowKey]);

  const isSelectAll = useMemo(() => {
    const selectedData = rowSelection?.selectedData || [];
    const selectedIdsInPage = selectedData
      .map((item) => item[rowKey] as number)
      .filter((id) => pageDataIds.includes(id));
    return selectedIdsInPage.length && selectedIdsInPage.length === pageDataIds.length;
  }, [rowSelection?.selectedData, pageDataIds]);

  const selectedIds = useMemo(
    () => (rowSelection?.selectedData || []).map((item) => item[rowKey]),
    [rowSelection?.selectedData, data]
  );

  const handleCheckedRowChange =
    (item: T) =>
    ({ checked }: { checked: boolean }) => {
      let selectedData = [...(rowSelection?.selectedData || [])];
      selectedData = checked
        ? [...selectedData, item]
        : selectedData.filter((sl) => sl[rowKey] !== item[rowKey]);
      rowSelection?.onChange(selectedData);
    };

  const handleCheckedAllChange = useCallback(
    ({ checked }: { checked: boolean }) => {
      let selectedData = [...(rowSelection?.selectedData || [])];
      const dataIds = data.map((item) => item[rowKey]);
      selectedData = checked
        ? [...selectedData, ...data.filter((item) => !selectedIds.includes(item[rowKey]))]
        : selectedData.filter((item) => !dataIds.includes(item[rowKey]));
      rowSelection?.onChange(selectedData);
    },
    [selectedIds, rowSelection?.selectedData, data]
  );

  const handleGoDetail = (item: T) => {
    if (editPath === 'salons') {
      navigate(`/clients/${clientId}/${editPath}/${item[rowKey]}/edit`);
    } else if (editPath) {
      navigate(`${editPath}/${item[rowKey]}/edit`);
    } else {
      rowClick?.(item);
    }
  };

  const Root = scroll ? Table.ScrollArea : Box;
  const resolvedMaxH = typeof maxH === 'string' ? maxH : `${maxH}px`;
  const resolvedMinH = typeof minH === 'string' ? minH : `${minH}px`;
  const addColSpan = hasExpand || hasSelection ? 1 : 0;

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container || !loadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_NEAR_BOTTOM_THRESHOLD) loadMore?.();
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSort = (key: string) => {
    const current = sortConfig.current;
    const isSameKey = current?.key === key;
    const direction = isSameKey && current?.direction === 'desc' ? 'asc' : 'desc';
    sortConfig.current = { key, direction };
    onSort?.(key, direction);
  };

  return (
    <Box width="full" className={className}>
      <Root
        ref={scroll ? scrollRef : undefined}
        className={scroll ? 'tbl-scroll' : ''}
        maxH={resolvedMaxH}
        minH={resolvedMinH}
      >
        <Table.Root size="lg" stickyHeader={scroll}>
          {!hideHeader && (
            <Table.Header>
              <Table.Row>
                {(hasExpand || hasSelection) && (
                  <Table.ColumnHeader width={rowSelection?.columnWidth || '60px'}>
                    {hasSelection && !rowSelection?.hideCheckAll && (
                      <Flex justifyContent="flex-start" alignItems="center">
                        <Checkbox
                          checked={isSelectAll}
                          variant="solid"
                          colorPalette="teal"
                          onCheckedChange={handleCheckedAllChange}
                        />
                        {rowSelection?.textSelectAll ? trans(rowSelection.textSelectAll) : ''}
                      </Flex>
                    )}
                  </Table.ColumnHeader>
                )}
                {columns.map((col, index) => {
                  const align = col.align || (index === 0 ? 'left' : 'center');
                  return (
                    <Table.ColumnHeader
                      key={col.key}
                      align={align}
                      width={col.width}
                      className={align === 'center' ? 'text-center' : 'text-left'}
                      onClick={() => handleSort(col.key)}
                    >
                      <Flex
                        cursor="pointer"
                        alignItems="center"
                        justifyContent={align === 'center' ? 'center' : 'flex-start'}
                        gap={1}
                      >
                        <Box>{col.title}</Box>
                        {sortConfig.current?.key === col.key && (
                          <SortIcon isActive={true} direction={sortConfig.current.direction} />
                        )}
                      </Flex>
                    </Table.ColumnHeader>
                  );
                })}
              </Table.Row>
            </Table.Header>
          )}

          <Table.Body pos="relative">
            {loading && (
              <Box pos="absolute" top="50px" height="100px" inset="0" bg="bg/80">
                <Center h="full">
                  <Spinner color="teal.500" />
                </Center>
              </Box>
            )}

            {data.map((item, index) =>
              renderExpand ? (
                <RowExpand<T>
                  key={item[rowKey] as string}
                  item={item}
                  index={index}
                  renderExpand={renderExpand}
                  columns={columns}
                  disableRowClickExpand={disableRowClickExpand}
                  handleGoDetail={rowClick || editPath ? handleGoDetail : undefined}
                  colSpan={columns.length + addColSpan}
                />
              ) : (
                <Table.Row
                  key={item[rowKey] as string}
                  onClick={rowClick || editPath ? () => handleGoDetail(item) : undefined}
                  className={rowClick || editPath ? 'pointer' : ''}
                >
                  {hasSelection && (
                    <Table.Cell
                      width={rowSelection?.columnWidth || '60px'}
                      className={
                        (rowSelection?.columnClassName || '') +
                        ((item as any).disabled ? ' disable-checkbox' : '')
                      }
                    >
                      <Checkbox
                        checked={selectedIds.includes(item[rowKey])}
                        variant="solid"
                        colorPalette="teal"
                        onCheckedChange={handleCheckedRowChange(item)}
                        disabled={(item as any).disabled}
                      />
                    </Table.Cell>
                  )}
                  {columns.map((col, index) => {
                    const align = col.align || (index === 0 ? 'left' : 'center');
                    return (
                      <Table.Cell
                        key={col.key as string}
                        align={align}
                        width={col.width}
                        className={align === 'center' ? 'text-center' : 'text-left'}
                      >
                        {col.render
                          ? col.render(
                              item,
                              data.findIndex((x) => x[rowKey] === item[rowKey])
                            )
                          : ''}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              )
            )}

            {!data.length && !loading && !hideNoData && (
              <Table.Row>
                <Table.Cell
                  paddingTop={30}
                  borderBottom="none"
                  colSpan={columns.length + addColSpan}
                  className='empty-table'
                >
                  <Center h="full">
                    <EmptyState icon={<GiArchiveResearch />} description={trans('shared.nodata')} />
                  </Center>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>

          {renderFooter && (
            <Table.Footer>
              <Table.Row>
                <Table.Cell colSpan={columns.length + addColSpan} className="p0">
                  {renderFooter}
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table.Root>
      </Root>

      {total && (
        <BasePagination
          total={total}
          pageIndex={page}
          pageSize={size}
          onPageChange={onPageChange}
        />
      )}
    </Box>
  );
}

export { BaseTable };
